import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "className", "lg", "md", "prefix", "pull", "push", "sm", "span", "suffix", "xs"];

/** @jsx jsx */
import { css, jsx, useTheme } from '@emotion/react';
import React from 'react';
import RowContext from './RowContext';

function percentage(value) {
  return "".concat(value * (100 / 12), "%");
}

function negate(value) {
  return value * -1;
}

function width(layout) {
  if (layout.span != null && layout.span > 0) return percentage(layout.span);
  return undefined;
}

function position(layout) {
  if (layout.pull != null || layout.push != null) return 'relative';
  return undefined;
}

function reposition(layout) {
  if (layout.push != null) return percentage(layout.push);
  if (layout.pull != null) return percentage(negate(layout.pull));
  return undefined;
}

function marginInlineStart(layout) {
  if (layout.prefix != null) return percentage(layout.prefix);
  return undefined;
}

function marginInlineEnd(layout) {
  if (layout.suffix != null) return percentage(layout.suffix);
  return undefined;
}

function layoutForProps(props) {
  var span = props.span,
      pull = props.pull,
      push = props.push,
      prefix = props.prefix,
      suffix = props.suffix,
      xs = props.xs,
      sm = props.sm,
      md = props.md,
      lg = props.lg;
  var layout = {
    xs: {},
    sm: {},
    md: {},
    lg: {}
  };

  if (xs != null) {
    layout.xs = typeof xs === 'number' ? {
      span: xs
    } : xs;
  }

  if (sm != null) {
    layout.sm = typeof sm === 'number' ? {
      span: sm
    } : sm;
  }

  if (md != null) {
    layout.md = typeof md === 'number' ? {
      span: md
    } : md;
  }

  if (lg != null) {
    layout.lg = typeof lg === 'number' ? {
      span: lg
    } : lg;
  }

  if (span != null) {
    layout.xs.span = span;
  }

  if (pull != null) {
    layout.xs.pull = pull;
  }

  if (push != null) {
    layout.xs.push = push;
  }

  if (prefix != null) {
    layout.xs.prefix = prefix;
  }

  if (suffix != null) {
    layout.xs.suffix = suffix;
  }

  return layout;
}

var Column = function Column(props) {
  var _css;

  var children = props.children,
      className = props.className,
      _props$lg = props.lg,
      lg = _props$lg === void 0 ? {} : _props$lg,
      _props$md = props.md,
      md = _props$md === void 0 ? {} : _props$md,
      prefix = props.prefix,
      pull = props.pull,
      push = props.push,
      _props$sm = props.sm,
      sm = _props$sm === void 0 ? {} : _props$sm,
      span = props.span,
      suffix = props.suffix,
      _props$xs = props.xs,
      xs = _props$xs === void 0 ? {} : _props$xs,
      unhandledProps = _objectWithoutProperties(props, _excluded);

  var theme = useTheme();
  var row = React.useContext(RowContext);
  var layout = layoutForProps(props);
  var rootStyles = /*#__PURE__*/css((_css = {
    float: "".concat(theme.direction === 'ltr' ? 'left' : 'right'),
    display: layout.xs.span === 0 ? 'none' : undefined,
    width: width(layout.xs),
    position: position(layout.xs),
    left: theme.direction === 'ltr' ? reposition(layout.xs) : undefined,
    right: theme.direction === 'rtl' ? reposition(layout.xs) : undefined,
    marginLeft: theme.direction === 'ltr' ? marginInlineStart(layout.xs) : marginInlineEnd(layout.xs),
    marginRight: theme.direction === 'rtl' ? marginInlineStart(layout.xs) : marginInlineEnd(layout.xs),
    marginInlineStart: marginInlineStart(layout.xs),
    marginInlineEnd: marginInlineEnd(layout.xs),
    paddingTop: row.gutter ? "".concat(theme.size.grid.rowGap, "px") : undefined,
    paddingBottom: row.gutter ? "".concat(theme.size.grid.rowGap, "px") : undefined,
    paddingLeft: row.gutter ? "".concat(theme.size.grid.columnGap, "px") : undefined,
    paddingRight: row.gutter ? "".concat(theme.size.grid.columnGap, "px") : undefined,
    paddingInlineStart: row.gutter ? "".concat(theme.size.grid.columnGap, "px") : undefined,
    paddingInlineEnd: row.gutter ? "".concat(theme.size.grid.columnGap, "px") : undefined,
    boxSizing: row.gutter ? 'border-box' : undefined
  }, _defineProperty(_css, theme.breakpoints.up('sm'), {
    width: width(layout.sm),
    position: position(layout.sm),
    left: theme.direction === 'ltr' ? reposition(layout.sm) : undefined,
    right: theme.direction === 'rtl' ? reposition(layout.sm) : undefined,
    marginLeft: theme.direction === 'ltr' ? marginInlineStart(layout.sm) : marginInlineEnd(layout.sm),
    marginRight: theme.direction === 'rtl' ? marginInlineStart(layout.sm) : marginInlineEnd(layout.sm),
    marginInlineStart: marginInlineStart(layout.sm),
    marginInlineEnd: marginInlineEnd(layout.sm)
  }), _defineProperty(_css, theme.breakpoints.up('md'), {
    width: width(layout.md),
    position: position(layout.md),
    left: theme.direction === 'ltr' ? reposition(layout.md) : undefined,
    right: theme.direction === 'rtl' ? reposition(layout.md) : undefined,
    marginLeft: theme.direction === 'ltr' ? marginInlineStart(layout.md) : marginInlineEnd(layout.md),
    marginRight: theme.direction === 'rtl' ? marginInlineStart(layout.md) : marginInlineEnd(layout.md),
    marginInlineStart: marginInlineStart(layout.md),
    marginInlineEnd: marginInlineEnd(layout.md)
  }), _defineProperty(_css, theme.breakpoints.up('lg'), {
    width: width(layout.lg),
    position: position(layout.lg),
    left: theme.direction === 'ltr' ? reposition(layout.lg) : undefined,
    right: theme.direction === 'rtl' ? reposition(layout.lg) : undefined,
    marginLeft: theme.direction === 'ltr' ? marginInlineStart(layout.lg) : marginInlineEnd(layout.lg),
    marginRight: theme.direction === 'rtl' ? marginInlineStart(layout.lg) : marginInlineEnd(layout.lg),
    marginInlineStart: marginInlineStart(layout.lg),
    marginInlineEnd: marginInlineEnd(layout.lg)
  }), _css), process.env.NODE_ENV === "production" ? "" : ";label:rootStyles;", process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
  return jsx("div", _extends({}, unhandledProps, {
    className: className,
    css: rootStyles
  }), children);
};

Column.displayName = 'Column';
export default Column;